import { useWatch } from '@kanda-libs/ks-component-ts';
import { FinanceRate } from '@kanda-libs/ks-frontend-services';
import { useMemo } from 'react';
import { calculateLoanCosts } from 'utils/Finance-functions';
import { FinanceInfo } from './types';

export type FinanceInfoHook = FinanceInfo;

export default function useFinanceInfo(
  rates: FinanceRate[],
  bnplRates?: FinanceRate[],
): FinanceInfoHook {
  const [apr, price, duration, depositPercentage] = useWatch({
    name: ['apr', 'price', 'duration', 'deposit'],
  });

  const depositPct = depositPercentage / 100;
  const amountOfCredit = price - price * depositPct;
  const priceAmount = parseInt(price, 10);

  const aprAmount = useMemo(() => {
    if (apr === 'BNPL') {
      return Math.round(parseFloat(duration.split(' + ')?.[2] || '0') * 100);
    }
    return parseInt(apr, 10);
  }, [apr, duration]);

  const durationAmount = useMemo(() => {
    if (apr === 'BNPL') {
      return parseInt(duration.split(' + ')?.[1] || '0', 10);
    }
    return parseInt(duration, 10);
  }, [apr, duration]);

  const deferredDurationAmount = useMemo(() => {
    if (apr === 'BNPL') {
      return parseInt(duration.split(' + ')?.[0] || '0', 10);
    }
    return undefined;
  }, [apr, duration]);

  const rate = useMemo(() => {
    if (apr === 'BNPL' && bnplRates)
      return bnplRates?.find(
        (currentRate) =>
          currentRate.apr === aprAmount &&
          currentRate.duration === durationAmount &&
          currentRate.deferred_duration === deferredDurationAmount,
      );
    return rates.find(
      (currentRate) =>
        currentRate.apr === aprAmount &&
        currentRate.duration === durationAmount,
    );
  }, [
    apr,
    aprAmount,
    durationAmount,
    rates,
    bnplRates,
    deferredDurationAmount,
  ]);

  const costs = useMemo(
    () =>
      rate
        ? calculateLoanCosts(
            {
              totalExcVat: priceAmount,
              totalIncVat: priceAmount,
              totalVat: 0,
            },
            rate,
            priceAmount * depositPct,
          )
        : undefined,
    [priceAmount, rate, depositPct],
  );

  const monthlyPayment = costs?.monthly || 0;
  const interest = costs?.financeCost || 0;
  const totalPayable = price + interest;

  return {
    amountOfCredit,
    monthlyPayment,
    totalPayable,
    interest,
    price,
    apr: aprAmount,
    duration: durationAmount,
    deferredDuration: deferredDurationAmount,
    deposit: depositPct,
    formCompleted: !!price && !!apr && !!duration && !!depositPercentage,
  };
}
